<template>
  <div class="px-2 py-4" ref="root" :data-testid="`event-tooltip-${event.id}`">
    <div class="header mb-2">
      <div class="d-flex align-center">
        <v-icon class="mr-2 text-white">mdi-truck</v-icon>
        <strong>{{ companyName }}</strong>
        <template v-if="event.isRecurringAppointment">
          <v-spacer></v-spacer>
          <v-icon size="small" color="white" class="mr-1">mdi-repeat</v-icon>
          <span class="font-size-x-small uppercase">Recurring</span>
        </template>
      </div>
      <span class="date-label">{{ date }}</span>
    </div>

    <table class="event-tooltip-table">
      <thead></thead>
      <tbody>
        <tr>
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.confirmationNumber') }}:</td>
          <td class="font-weight-bold">{{ event.confirmationNumber }}</td>
        </tr>
        <tr>
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.status') }}:</td>
          <td>
            <v-chip
              label
              size="x-small"
              :class="statusStyles.textColor"
              :color="statusStyles.bgColor">
              <v-icon
                size="x-small"
                color="requested darken-3"
                class="requested-icon"
                v-if="novaCore.isRequested(event.status)">
                mdi-clock-alert
              </v-icon>
              {{ $t(`global.appointmentStatuses.${event.status}`) }}
            </v-chip>
          </td>
        </tr>
        <tr>
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.tags') }}:</td>
          <td class="font-weight-bold">
            <div v-if="tags && tags.length">
              <template v-for="tag in tags" :key="tag.name">
                <v-chip
                  v-if="tag.color"
                  label
                  size="x-small"
                  class="px-2 font-weight-bold mr-1"
                  :color="tag.color"
                  :style="{ color: tag.textColor }">
                  {{ tag.name }}
                </v-chip>
                <v-chip v-else label size="x-small" class="px-2" variant="outlined">
                  {{ tag.name }}
                </v-chip>
              </template>
            </div>
            <span v-else>----</span>
          </td>
        </tr>
        <tr v-if="event.eta">
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.eta') }}:</td>
          <td>
            {{ novaCore.renderUtcInTimezone(event.eta, event.dock.warehouse.timezone) }}
          </td>
        </tr>
        <tr v-if="translatedRefNumberDisplayName">
          <td class="pr-4">{{ translatedRefNumberDisplayName }}:</td>
          <td class="font-weight-bold">{{ event.refNumber }}</td>
        </tr>
        <tr>
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.dock') }}:</td>
          <td class="font-weight-bold">{{ event.readableDockName }}</td>
        </tr>
        <tr>
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.loadType') }}:</td>
          <td class="font-weight-bold">
            {{ event.loadType.name }} - {{ event.loadType.direction }}
          </td>
        </tr>
        <tr>
          <td class="pr-4">{{ $t('grid.events.tooltipLabels.duration') }}:</td>
          <td class="font-weight-bold">{{ getEventDurationString(event) }}</td>
        </tr>
        <tr v-for="(field, index) in limitedCustomFields" :key="`custom-field-popup-${index}`">
          <td class="pr-4">{{ field.label }}:</td>
          <td class="font-weight-bold">
            <span class="text-pre-wrap" v-html="getFieldValue(field)"></span>
          </td>
        </tr>
        <tr v-if="customFieldOverflowCount">
          <td class="font-weight-bold">+ {{ customFieldOverflowCount }} more</td>
          <td></td>
        </tr>

        <tr v-if="isLoadingCustomFormsData">
          <td colspan="2">
            <v-progress-circular indeterminate color="primary" size="15" />
          </td>
        </tr>
        <template v-else-if="customFormsData.length > 0">
          <tr v-for="formField of limitedCustomFormsFields" :key="formField.id">
            <td class="pr-4">{{ formField.label }}:</td>
            <td class="font-weight-bold">
              <span class="text-pre-wrap" v-html="formField.value"></span>
            </td>
          </tr>
          <tr v-if="customFormsFieldOverflowCount">
            <td class="font-weight-bold">+ {{ customFormsFieldOverflowCount }} more</td>
            <td></td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  AppointmentStatus,
  CustomFormsFeatures,
  getTranslatedRefNumberDisplayName
} from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service.js';
import { isEmpty, isNumber, isBoolean } from 'lodash';

const triggerFeaturesToFetch = [CustomFormsFeatures.BookLoadType];
const statusesWithBlackText = ['cancelled', 'requested', 'inprogress'];

/**
 * Calendar event tooltip
 * @displayName Event Tooltip
 */
export default {
  props: {
    /**
     * Calendar event
     */
    event: {
      type: Object,
      required: true
    }
  },
  computed: {
    date() {
      const tz = null;
      const date = this.novaCore.getFormattedTime(
        this.event.start,
        this.novaCore.LuxonDateTimeFormats.LongDateLongMonth,
        this.$i18n.locale
      );
      const start = this.novaCore.formatDateTimeWithMilitarySupport(
        this.event.start,
        tz,
        this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
        this.$isMilitaryTimeEnabled(this.$org),
        this.novaCore.LuxonDateTimeFormats.Extended24HrTime,
        this.$i18n.locale
      );
      const end = this.novaCore.formatDateTimeWithMilitarySupport(
        this.event.end,
        tz,
        this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
        this.$isMilitaryTimeEnabled(this.$org),
        this.novaCore.LuxonDateTimeFormats.Extended24HrTime,
        this.$i18n.locale
      );

      let timeString = `${date} •  ${start}`;
      if (this.event.isInDSTChange) {
        timeString += ` (${this.event.startTimezone})`;
      }
      timeString += ` - ${end} (${this.event.endTimezone})`;
      return timeString;
    },
    tags() {
      if (!this.event.tags?.length) {
        return [];
      }

      const allTags = this.novaCore.deepClone(this.event.tags);
      const customTags = allTags.filter(t =>
        Boolean(this.util.getCustomTagData(this.$org.customTags, t))
      );
      // Sort custom tags to front but keep the original order overall
      const tags = [...new Set([...customTags, ...allTags])];
      // TODO part of all of this logic is duplicated in the calendar store - abstract
      const formattedTags = tags.map(tag => {
        const rescheduledRegExp =
          /^Rescheduled - [0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z$/;
        if (tag.match(rescheduledRegExp)) {
          tag = 'Rescheduled';
        }
        const customTagData = this.util.getCustomTagData(this.$org.customTags, tag);

        return {
          name: tag,
          color: customTagData?.color || null,
          textColor: customTagData?.textColor || null
        };
      });

      // Removes duplicated tags (e.g. #Rescheduled, #Rescheduled)
      const uniqueFormattedTags = [...new Set(formattedTags)];

      return uniqueFormattedTags || [];
    },
    customFormsFieldOverflowCount() {
      return this.customFormsData.length - this.limitedCustomFormsFields.length;
    },
    limitedCustomFormsFields() {
      return this.customFormsData.slice(0, this.customFieldsToDisplay);
    },
    customFieldOverflowCount() {
      return this.customFieldsWithValue.length - this.limitedCustomFields.length;
    },
    limitedCustomFields() {
      return this.customFieldsWithValue.slice(0, this.customFieldsToDisplay);
    },
    customFieldsWithValue() {
      return this.event.customFields?.length > 0
        ? this.event.customFields.filter(field => {
            return !!field.value;
          })
        : [];
    },
    statusStyles() {
      let bgColor = this.event.status.toLowerCase();
      let textColor = statusesWithBlackText.includes(bgColor) ? 'black--text' : 'white--text';
      return { bgColor: bgColor, textColor: textColor };
    },
    companyName() {
      return this.event.user?.company?.name || 'No carrier';
    },
    getGenericTagClass() {
      const darkStatusColors = [
        AppointmentStatus.Arrived,
        AppointmentStatus.NoShow,
        AppointmentStatus.Completed,
        AppointmentStatus.Scheduled
      ];
      return darkStatusColors.includes(this.event?.status);
    },
    translatedRefNumberDisplayName() {
      const displayName = this.$getSettingValue(
        'referenceNumberDisplayName',
        this.event.dock.warehouse.settings
      );
      return getTranslatedRefNumberDisplayName(displayName);
    }
  },
  data() {
    return {
      customFieldsToDisplay: 5,
      customFormsData: [],
      isLoadingCustomFormsData: false,
      isCustomFormsDataFetched: false
    };
  },
  methods: {
    getFieldValue(field) {
      return this.novaCore.getCustomFieldFormattedValue(field, {
        [this.novaCore.CustomFieldType.Timestamp]: {
          timezone: this.event.dock.warehouse.timezone,
          formatAsMilitary: this.$isMilitaryTimeEnabled(this.$org)
        }
      });
    },
    async getCustomFormsData() {
      if (this.isCustomFormsDataFetched) {
        return;
      }

      this.isLoadingCustomFormsData = true;

      try {
        const formData = await customFormsService.getCustomFormData({
          s: {
            objectId: this.event.id,
            'trigger.feature': {
              $in: triggerFeaturesToFetch
            }
          },
          join: ['trigger'],
          limit: 1000
        });
        if (formData?.length) {
          this.customFormsData = formData
            .sort((a, b) => a.formField.order - b.formField.order)
            .map(data => {
              data.label = data.formField?.overrideLabel || data.label;
              data.value = this.getFieldValue(data);
              return data;
            })
            .filter(data => isBoolean(data.value) || isNumber(data.value) || !isEmpty(data.value));
        } else {
          this.customFormsData = [];
        }
        this.isCustomFormsDataFetched = true;
      } catch (error) {
        console.error('Error fetching custom forms data:', error);
      } finally {
        this.isLoadingCustomFormsData = false;
      }
    },
    getEventDurationString(event) {
      const duration = this.novaCore.duration_min(event);
      return this.$t('grid.events.tooltipValues.duration', { durationMin: duration });
    },
    invalidateFormData(formData) {
      // Invalidate the form data if the objectId matches the event id
      if (formData?.objectId === this.event.id) {
        this.isCustomFormsDataFetched = false;
      }
    }
  },
  mounted() {
    // This is needed to observe the tooltip visibility and fetch custom forms data when it is fully visible
    // Because all the tooltips are mounted at once, and we would fetch all the data at once
    // which is not ideal for performance
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio === 1) {
          this.getCustomFormsData();
        }
      },
      { root: null, threshold: 1 }
    );

    if (this.$refs.root) {
      this.observer.observe(this.$refs.root);
    }

    this.$eventHub.$on('customformdata-updated', this.invalidateFormData);
    this.$eventHub.$on(`update-CustomFormData`, this.invalidateFormData);
    this.$eventHub.$on(`create-CustomFormData`, this.invalidateFormData);
  },
  beforeUnmount() {
    if (this.observer && this.$refs.root) {
      this.observer.unobserve(this.$refs.root);
    }

    this.$eventHub.$off('customformdata-updated', this.invalidateFormData);
    this.$eventHub.$off(`update-CustomFormData`, this.invalidateFormData);
    this.$eventHub.$off(`create-CustomFormData`, this.invalidateFormData);
  }
};
</script>
